import React, { type ReactNode, useEffect } from 'react';
import { InlineSpinner } from '@vds/components';

import { appLogger } from '@/utils/app-logger/appLogger';

import ErrorBoundaryContent from './ErrorBoundaryContent';

// One client attempt is enough to recover the common case: the render failed
// against a stale/unavailable remote container, but the browser can load a fresh
// remoteEntry.js (the federation runtime evicts + re-fetches the entry on the
// failed load) and re-render the subtree successfully.
const MAX_CLIENT_RETRIES = 1;

interface RemotePageBoundaryProps {
    children: ReactNode;
    /** Remote module id (e.g. `hubs/shows/slug`) included in failure logs. */
    remoteModule?: string;
    /**
     * Set by `getRemotePageServerSideProps` when the remote's SSR data load
     * failed. The boundary then renders the fallback during SSR instead of
     * attempting to render a known-broken remote (which would throw a hard 500),
     * and lets the client attempt the remote once after mount.
     */
    remoteUnavailable?: boolean;
    /**
     * When this value changes the boundary clears a prior failure. Pass the
     * current route (e.g. `router.asPath`) so navigating away from a broken
     * remote page renders the next page fresh instead of showing its fallback.
     */
    resetKey?: string;
    fallback?: ReactNode;
    loadingFallback?: ReactNode;
}

interface RemotePageBoundaryState {
    hasError: boolean;
    retryCount: number;
    resetKey?: string;
}

/**
 * Isolates a federated remote page render to prevent hard 500 errors during SSR
 * if a remote container is stale or missing.
 * Catches SSR render throws, renders a neutral `loadingFallback` on the client
 * during a single silent retry (preventing an error UI flash), and falls back
 * to the terminal error `fallback` only if the retry fails.
 */
class RemotePageBoundary extends React.Component<RemotePageBoundaryProps, RemotePageBoundaryState> {
    constructor(props: RemotePageBoundaryProps) {
        super(props);
        this.state = {
            // Start in the fallback state when SSR already determined the remote
            // is unavailable, so the server never tries to render the broken remote.
            hasError: props.remoteUnavailable ?? false,
            retryCount: 0,
            resetKey: props.resetKey,
        };
    }

    static getDerivedStateFromError(): Partial<RemotePageBoundaryState> {
        return { hasError: true };
    }

    static getDerivedStateFromProps(
        props: RemotePageBoundaryProps,
        state: RemotePageBoundaryState,
    ): Partial<RemotePageBoundaryState> | null {
        if (props.resetKey !== state.resetKey) {
            return {
                hasError: props.remoteUnavailable ?? false,
                retryCount: 0,
                resetKey: props.resetKey,
            };
        }
        return null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        appLogger.error(
            'Remote page failed to render',
            {
                remoteModule: this.props.remoteModule,
                componentStack: errorInfo.componentStack,
            },
            error,
        );
    }

    handleRetry = (): void => {
        this.setState((state) => ({ hasError: false, retryCount: state.retryCount + 1 }));
    };

    render(): ReactNode {
        if (this.state.hasError) {
            // A retry is still coming: show the loading placeholder, not the error UI
            const canRetry = this.state.retryCount < MAX_CLIENT_RETRIES;
            return (
                <RemoteErrorFallback canRetry={canRetry} onRetry={this.handleRetry}>
                    {canRetry
                        ? (this.props.loadingFallback ?? <RemoteLoadingFallback />)
                        : (this.props.fallback ?? <ErrorBoundaryContent />)}
                </RemoteErrorFallback>
            );
        }

        return this.props.children;
    }
}

function RemoteLoadingFallback(): JSX.Element {
    return (
        <div
            data-testid="remote-loading-fallback"
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
            }}>
            <InlineSpinner size="large" />
        </div>
    );
}

type RemoteErrorFallbackProps = PropsWithRetry & {
    children: ReactNode;
};

type PropsWithRetry = {
    canRetry: boolean;
    onRetry: () => void;
};

function RemoteErrorFallback({
    canRetry,
    onRetry,
    children,
}: RemoteErrorFallbackProps): JSX.Element {
    useEffect(() => {
        if (!canRetry) {
            return;
        }
        // Client-only: effects never run during SSR, so the server keeps
        // rendering the fallback. Once the fallback has painted on the client we
        // attempt the remote a single time; the federation runtime re-fetches a
        // fresh remoteEntry.js on the failed load, so the retry can succeed.
        onRetry();
    }, [canRetry, onRetry]);

    return <>{children}</>;
}

export default RemotePageBoundary;
